/**
 * Dynamic block OBJECTS writer — DYN-1.3.
 *
 * Re-emits a `DynamicBlockMetadata` instance as the flat list of
 * ACDBBLOCK* / ACDBEVALGRAPH / ACDBBLOCKREPRESENTATION objects that
 * AutoCAD expects in the DXF OBJECTS section. Tier-1 classes are
 * serialized fully; non-Tier-1 objects preserved in
 * `meta.futureExtensions.rawObjects` are re-appended verbatim so any
 * extensions round-trip without loss.
 *
 * Paired with `dxfBridge.dynamic.parser.ts` (symmetric decoder) and
 * `dxfBridge.dynamic.shared.ts` (group-emission helpers).
 */
import type { DxfDynamicBlockObject, DxfGroup } from "@dxf-ai-editor/dxf-io";

import type {
  BasePointParameter,
  DynamicBlockMetadata,
  FlipAction,
  FlipParameter,
  LinearParameter,
  MoveAction,
  StretchAction,
  VisibilityParameter,
  VisibilityState,
} from "./dynamicBlock";
import { pushGroup } from "./dxfBridge.dynamic.shared";
import { tryWriteConstraintBlockPropertiesTable } from "./dxfBridge.dynamic.writer.bpt";
import {
  writeAngularConstraintParameterBpt,
  writeHorizontalConstraintParameterBpt,
  writeRadialConstraintParameterBpt,
  writeVerticalConstraintParameterBpt,
} from "./dxfBridge.dynamic.writer.constraints";
import { writeGrip } from "./dxfBridge.dynamic.writer.grips";
import {
  tryWriteTier2Action,
  tryWriteTier2Parameter,
  writeBlockPropertiesTable,
  writeLookupTable,
  type Tier2WriterContext,
} from "./dxfBridge.dynamic.writer.tier2";
import {
  tryWriteTier3Action,
  tryWriteTier3Parameter,
  writeGeometricConstraint,
} from "./dxfBridge.dynamic.writer.tier3";
import { writeUserParameter } from "./dxfBridge.dynamic.writer.userParam";

// Phase 5.1.d — DynamicAspect registry migration. Importing the aspect
// modules registers them as a side effect; the registry-driven dispatch
// below replaces the direct writer calls when the legacy flag is off.
import { findAspectById } from "./dxfBridge.dynamic.aspect";
import "./dxfBridge.dynamic.aspect.grip";
import "./dxfBridge.dynamic.aspect.userParam";
import "./dxfBridge.dynamic.aspect.bpt";

/**
 * Phase 5.1 emergency rollback flag. Mirrors the parser-side constant;
 * see `dxfBridge.dynamic.parser.ts` for the rationale.
 */
const DYNAMIC_ASPECT_LEGACY = false;

// ═══════════════════════════════════════════════════════════════════════════
// Writer context
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Minimal writer context — allocates fresh handles for objects that do
 * not already have one. We start high enough (0x100000 = 1048576) to
 * avoid collisions with the 6-digit handles AutoCAD normally assigns.
 *
 * Tier 2 emitters widen this with a `tableIdToHandle` map so Lookup
 * parameters can point at their table's freshly-minted handle. The
 * shared shape is `Tier2WriterContext` in `writer.tier2.ts`; the
 * narrower Tier 1 functions still only use `blockRecordHandle` and
 * `nextHandle`.
 *
 * SL-6 (R2018) — when a `sharedNextHandle` minter is supplied (by
 * `exportDynamicBlockObjects` / the top-level export orchestrator), EVERY
 * block's synthesis draws from that ONE monotonic, collision-checked minter
 * so group-5 handles are globally unique ACROSS blocks. Without it (direct
 * unit-test callers) we fall back to a per-context counter starting at
 * 0x100000 — which is safe for a single block but WOULD restart per block if
 * used across a multi-block document (the `cross-block-handle-collision` P1).
 */
type WriterContext = Tier2WriterContext;

function makeContext(
  blockRecordHandle: string,
  sharedNextHandle?: () => string,
): WriterContext {
  let counter = 0x100000;
  const nextHandle =
    sharedNextHandle ??
    ((): string => {
      counter += 1;
      return counter.toString(16).toUpperCase();
    });
  return {
    blockRecordHandle,
    nextHandle,
    tableIdToHandle: new Map<string, string>(),
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Per-class emitters
// ═══════════════════════════════════════════════════════════════════════════

function writeLinearParameter(
  p: LinearParameter,
  ctx: WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  pushGroup(groups, 0, "ACDBBLOCKLINEARPARAMETER");
  const handle = p.id || ctx.nextHandle();
  pushGroup(groups, 5, handle);
  pushGroup(groups, 330, ctx.blockRecordHandle);
  pushGroup(groups, 100, "AcDbEvalExpr");
  pushGroup(groups, 100, "AcDbBlockElement");
  pushGroup(groups, 100, "AcDbBlockParameter");
  pushGroup(groups, 100, "AcDbBlock1PtParameter");
  pushGroup(groups, 100, "AcDbBlock2PtParameter");
  pushGroup(groups, 100, "AcDbBlockLinearParameter");
  pushGroup(groups, 1, p.name);
  if (p.label) pushGroup(groups, 3, p.label);
  if (p.description) pushGroup(groups, 4, p.description);
  pushGroup(groups, 10, p.basePoint.x);
  pushGroup(groups, 20, p.basePoint.y);
  pushGroup(groups, 30, 0);
  pushGroup(groups, 11, p.secondPoint.x);
  pushGroup(groups, 21, p.secondPoint.y);
  pushGroup(groups, 31, 0);
  pushGroup(groups, 140, p.defaultDistance);
  pushGroup(groups, 141, p.currentDistance);
  const mode =
    p.valueSet.mode === "increment" ? 1 : p.valueSet.mode === "list" ? 2 : 0;
  pushGroup(groups, 1071, mode);
  pushGroup(groups, 142, p.valueSet.increment);
  if (p.valueSet.min !== undefined) pushGroup(groups, 143, p.valueSet.min);
  if (p.valueSet.max !== undefined) pushGroup(groups, 144, p.valueSet.max);
  for (const v of p.valueSet.listValues) pushGroup(groups, 145, v);
  pushGroup(groups, 280, p.chainActions ? 1 : 0);
  pushGroup(groups, 281, p.showGrip ? 1 : 0);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKLINEARPARAMETER",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

function writeFlipParameter(
  p: FlipParameter,
  ctx: WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  pushGroup(groups, 0, "ACDBBLOCKFLIPPARAMETER");
  const handle = p.id || ctx.nextHandle();
  pushGroup(groups, 5, handle);
  pushGroup(groups, 330, ctx.blockRecordHandle);
  pushGroup(groups, 100, "AcDbEvalExpr");
  pushGroup(groups, 100, "AcDbBlockElement");
  pushGroup(groups, 100, "AcDbBlockParameter");
  pushGroup(groups, 100, "AcDbBlock2PtParameter");
  pushGroup(groups, 100, "AcDbBlockFlipParameter");
  pushGroup(groups, 1, p.name);
  if (p.label) pushGroup(groups, 3, p.label);
  if (p.description) pushGroup(groups, 4, p.description);
  pushGroup(groups, 10, p.axisStart.x);
  pushGroup(groups, 20, p.axisStart.y);
  pushGroup(groups, 30, 0);
  pushGroup(groups, 11, p.axisEnd.x);
  pushGroup(groups, 21, p.axisEnd.y);
  pushGroup(groups, 31, 0);
  pushGroup(groups, 280, p.defaultValue ? 1 : 0);
  pushGroup(groups, 281, p.currentValue ? 1 : 0);
  pushGroup(groups, 282, p.chainActions ? 1 : 0);
  pushGroup(groups, 283, p.showGrip ? 1 : 0);
  pushGroup(groups, 300, p.flipLabel);
  pushGroup(groups, 301, p.baseLabel);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKFLIPPARAMETER",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

function writeVisibilityState(
  state: VisibilityState,
  paramHandle: string,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  pushGroup(groups, 0, "ACDBBLOCKVISIBILITYSTATE");
  pushGroup(groups, 5, state.id);
  pushGroup(groups, 330, paramHandle);
  pushGroup(groups, 100, "AcDbBlockVisibilityState");
  pushGroup(groups, 2, state.name);
  pushGroup(groups, 91, state.invisibleEntityIds.length);
  for (const id of state.invisibleEntityIds) pushGroup(groups, 340, id);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKVISIBILITYSTATE",
    handle: state.id,
    ownerHandle: paramHandle,
    rawGroups: groups,
  };
}

function writeVisibilityParameter(
  p: VisibilityParameter,
  ctx: WriterContext,
): DxfDynamicBlockObject[] {
  const paramGroups: DxfGroup[] = [];
  pushGroup(paramGroups, 0, "ACDBBLOCKVISIBILITYPARAMETER");
  const handle = p.id || ctx.nextHandle();
  pushGroup(paramGroups, 5, handle);
  pushGroup(paramGroups, 330, ctx.blockRecordHandle);
  pushGroup(paramGroups, 100, "AcDbEvalExpr");
  pushGroup(paramGroups, 100, "AcDbBlockElement");
  pushGroup(paramGroups, 100, "AcDbBlockParameter");
  pushGroup(paramGroups, 100, "AcDbBlock1PtParameter");
  pushGroup(paramGroups, 100, "AcDbBlockVisibilityParameter");
  pushGroup(paramGroups, 1, p.name);
  if (p.label) pushGroup(paramGroups, 3, p.label);
  if (p.description) pushGroup(paramGroups, 4, p.description);
  pushGroup(paramGroups, 10, p.position.x);
  pushGroup(paramGroups, 20, p.position.y);
  pushGroup(paramGroups, 30, 0);
  pushGroup(paramGroups, 91, p.states.length);
  pushGroup(paramGroups, 340, p.defaultStateId);
  pushGroup(paramGroups, 341, p.currentStateId);
  pushGroup(paramGroups, 280, p.chainActions ? 1 : 0);
  pushGroup(paramGroups, 281, p.showGrip ? 1 : 0);

  const result: DxfDynamicBlockObject[] = [
    {
      kind: "dynamic-block-object",
      type: "ACDBBLOCKVISIBILITYPARAMETER",
      handle,
      ownerHandle: ctx.blockRecordHandle,
      rawGroups: paramGroups,
    },
  ];
  for (const state of p.states) {
    result.push(writeVisibilityState(state, handle));
  }
  return result;
}

function writeBasePointParameter(
  p: BasePointParameter,
  ctx: WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  pushGroup(groups, 0, "ACDBBLOCKBASEPOINTPARAMETER");
  const handle = p.id || ctx.nextHandle();
  pushGroup(groups, 5, handle);
  pushGroup(groups, 330, ctx.blockRecordHandle);
  pushGroup(groups, 100, "AcDbEvalExpr");
  pushGroup(groups, 100, "AcDbBlockElement");
  pushGroup(groups, 100, "AcDbBlockParameter");
  pushGroup(groups, 100, "AcDbBlock1PtParameter");
  pushGroup(groups, 100, "AcDbBlockBasepointParameter");
  pushGroup(groups, 1, p.name);
  if (p.label) pushGroup(groups, 3, p.label);
  if (p.description) pushGroup(groups, 4, p.description);
  pushGroup(groups, 10, p.position.x);
  pushGroup(groups, 20, p.position.y);
  pushGroup(groups, 30, 0);
  pushGroup(groups, 281, p.showGrip ? 1 : 0);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKBASEPOINTPARAMETER",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

function writeMoveAction(
  a: MoveAction,
  paramHandle: string,
  ctx: WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  pushGroup(groups, 0, "ACDBBLOCKMOVEACTION");
  const handle = a.id || ctx.nextHandle();
  pushGroup(groups, 5, handle);
  pushGroup(groups, 330, paramHandle);
  pushGroup(groups, 100, "AcDbEvalExpr");
  pushGroup(groups, 100, "AcDbBlockElement");
  pushGroup(groups, 100, "AcDbBlockAction");
  pushGroup(groups, 100, "AcDbBlockActionWithBasePt");
  pushGroup(groups, 100, "AcDbBlockMoveAction");
  if (a.name) pushGroup(groups, 1, a.name);
  pushGroup(groups, 340, paramHandle);
  pushGroup(groups, 91, a.selectionEntityIds.length);
  for (const id of a.selectionEntityIds) pushGroup(groups, 340, id);
  pushGroup(groups, 440, a.distanceMultiplier);
  pushGroup(groups, 441, a.angleOffset);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKMOVEACTION",
    handle,
    ownerHandle: paramHandle,
    rawGroups: groups,
  };
}

function writeStretchAction(
  a: StretchAction,
  paramHandle: string,
  ctx: WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  pushGroup(groups, 0, "ACDBBLOCKSTRETCHACTION");
  const handle = a.id || ctx.nextHandle();
  pushGroup(groups, 5, handle);
  pushGroup(groups, 330, paramHandle);
  pushGroup(groups, 100, "AcDbEvalExpr");
  pushGroup(groups, 100, "AcDbBlockElement");
  pushGroup(groups, 100, "AcDbBlockAction");
  pushGroup(groups, 100, "AcDbBlockActionWithBasePt");
  pushGroup(groups, 100, "AcDbBlockStretchAction");
  if (a.name) pushGroup(groups, 1, a.name);
  pushGroup(groups, 340, paramHandle);
  pushGroup(groups, 91, a.selectionEntityIds.length);
  for (const id of a.selectionEntityIds) pushGroup(groups, 340, id);
  pushGroup(groups, 440, a.distanceMultiplier);
  pushGroup(groups, 441, a.angleOffset);
  pushGroup(groups, 10, a.crossingFrame.corner1.x);
  pushGroup(groups, 20, a.crossingFrame.corner1.y);
  pushGroup(groups, 30, 0);
  pushGroup(groups, 11, a.crossingFrame.corner2.x);
  pushGroup(groups, 21, a.crossingFrame.corner2.y);
  pushGroup(groups, 31, 0);
  pushGroup(groups, 50, a.stretchDirection);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKSTRETCHACTION",
    handle,
    ownerHandle: paramHandle,
    rawGroups: groups,
  };
}

function writeFlipAction(
  a: FlipAction,
  paramHandle: string,
  ctx: WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  pushGroup(groups, 0, "ACDBBLOCKFLIPACTION");
  const handle = a.id || ctx.nextHandle();
  pushGroup(groups, 5, handle);
  pushGroup(groups, 330, paramHandle);
  pushGroup(groups, 100, "AcDbEvalExpr");
  pushGroup(groups, 100, "AcDbBlockElement");
  pushGroup(groups, 100, "AcDbBlockAction");
  pushGroup(groups, 100, "AcDbBlockFlipAction");
  if (a.name) pushGroup(groups, 1, a.name);
  pushGroup(groups, 340, paramHandle);
  pushGroup(groups, 91, a.selectionEntityIds.length);
  for (const id of a.selectionEntityIds) pushGroup(groups, 340, id);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKFLIPACTION",
    handle,
    ownerHandle: paramHandle,
    rawGroups: groups,
  };
}

function writeBlockRepresentation(
  ctx: WriterContext,
  meta: DynamicBlockMetadata,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  pushGroup(groups, 0, "ACDBBLOCKREPRESENTATION");
  pushGroup(groups, 5, ctx.nextHandle());
  pushGroup(groups, 330, ctx.blockRecordHandle);
  pushGroup(groups, 100, "AcDbBlockRepresentation");
  pushGroup(groups, 90, meta.parameters.length);
  pushGroup(groups, 91, meta.actions.length);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKREPRESENTATION",
    handle: groups[1]!.value,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

function writeEvaluationGraph(
  ctx: WriterContext,
  meta: DynamicBlockMetadata,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  pushGroup(groups, 0, "ACDBEVALGRAPH");
  pushGroup(groups, 5, ctx.nextHandle());
  pushGroup(groups, 330, ctx.blockRecordHandle);
  pushGroup(groups, 100, "AcDbEvalGraph");
  pushGroup(groups, 96, meta.evaluationGraph.edges.length);
  for (const edge of meta.evaluationGraph.edges) {
    pushGroup(groups, 97, edge.parameterId);
    pushGroup(groups, 98, edge.actionId);
  }
  return {
    kind: "dynamic-block-object",
    type: "ACDBEVALGRAPH",
    handle: groups[1]!.value,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Top-level entry point
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Convert a `DynamicBlockMetadata` instance into the flat OBJECTS record
 * list AutoCAD expects. The order matches what we see from
 * AutoCAD-emitted DXFs: representation → eval graph → parameters (with
 * their child visibility states) → actions.
 *
 * SL-6 — `sharedNextHandle`, when supplied, is the ONE document-wide
 * monotonic handle minter every block's synthesis draws from (seeded above
 * the document's handle-max by the export orchestrator). Passing it makes
 * the minted group-5 handles globally unique across ALL dynamic blocks;
 * omitting it (unit-test callers with a single block) falls back to a
 * per-context 0x100000 counter.
 */
export function emitDynamicBlockObjects(
  blockRecordHandle: string,
  meta: DynamicBlockMetadata,
  sharedNextHandle?: () => string,
): DxfDynamicBlockObject[] {
  const ctx = makeContext(blockRecordHandle, sharedNextHandle);
  const out: DxfDynamicBlockObject[] = [];
  out.push(writeBlockRepresentation(ctx, meta));
  out.push(writeEvaluationGraph(ctx, meta));

  // Tables first — Lookup parameters (emitted below) reference them by
  // handle via ctx.tableIdToHandle. AutoCAD emits tables before their
  // referencing parameters so the resolver pass lands the pointer.
  for (const table of meta.lookupTables ?? []) {
    out.push(writeLookupTable(table, ctx));
  }
  if (meta.blockPropertiesTable) {
    // DYN-4 Phase 4a Fix 5 — prefer the constraint-based BPT writer
    // when the table carries `columns[]` / `rows[]` (the AutoCAD 2018+
    // shape). Fall back to the legacy BTABLE emitter for tables that
    // only populate `entries[]`.
    //
    // Phase 5.1.d — dispatch through the BPT aspect (write-only). The
    // aspect's `write` returns `undefined` for empty constraint tables;
    // the fallback to the legacy `entries[]` writer remains intact.
    const bptAspect = DYNAMIC_ASPECT_LEGACY
      ? undefined
      : findAspectById("properties-table");
    const constraintBpt = bptAspect
      ? (bptAspect.write(meta.blockPropertiesTable, ctx) ?? undefined)
      : tryWriteConstraintBlockPropertiesTable(meta.blockPropertiesTable, ctx);
    if (constraintBpt) {
      out.push(constraintBpt);
    } else {
      out.push(writeBlockPropertiesTable(meta.blockPropertiesTable, ctx));
    }
  }

  const paramHandleById = new Map<string, string>();
  for (const p of meta.parameters) {
    switch (p.kind) {
      case "linear": {
        const obj = writeLinearParameter(p, ctx);
        paramHandleById.set(p.id, obj.handle);
        out.push(obj);
        break;
      }
      case "flip": {
        const obj = writeFlipParameter(p, ctx);
        paramHandleById.set(p.id, obj.handle);
        out.push(obj);
        break;
      }
      case "visibility": {
        const objs = writeVisibilityParameter(p, ctx);
        paramHandleById.set(p.id, objs[0]!.handle);
        out.push(...objs);
        break;
      }
      case "basepoint": {
        const obj = writeBasePointParameter(p, ctx);
        paramHandleById.set(p.id, obj.handle);
        out.push(obj);
        break;
      }
      // ─── DYN-4 Phase 4a Fix 3 — BLOCKUSERPARAMETER ───────────────────────
      // Phase 5.1.d — route through the user-parameter aspect when
      // the registry is active.
      case "user": {
        const userParamAspect = DYNAMIC_ASPECT_LEGACY
          ? undefined
          : findAspectById("user-parameter");
        const obj =
          userParamAspect !== undefined
            ? (userParamAspect.write(p, ctx) as DxfDynamicBlockObject)
            : writeUserParameter(p, ctx);
        paramHandleById.set(p.id, obj.handle);
        out.push(obj);
        break;
      }
      // ─── DYN-4 Phase 4a — constraint parameter dispatch ──────────────────
      //
      // Every constraint kind has TWO writer variants:
      //   - Legacy (`tryWriteTier2Parameter` / `tryWriteTier3Parameter`)
      //     — emits the synthesised encoding used by pre-Phase-4 tests
      //     (group 1 for name, codes 10/11 for points, 170 for mode,
      //     142/143/144 for min/max/increment). Round-trip-safe for
      //     authored value sets with distinct `min` / `max` / `default`.
      //   - BPT (`dxfBridge.dynamic.writer.constraints.ts`) — emits the
      //     AutoCAD 2018+ BPT shape (300 name, 1010/1020 points, 140
      //     current, 175 mode, 141 min, 142 max, 143 increment). This is
      //     the shape АБИ1.dxf uses; its parser sibling (parser.constraints)
      //     reads 141 for both the authored default AND the value-set
      //     min, so BPT round-trip is exact only for `mode: "none"`
      //     parameters.
      //
      // Dispatch rule: prefer BPT when the parameter's value set uses
      // `mode: "none"` (matches AutoCAD's output and the parser's
      // no-conflict path) or when the value set is fully absent. Fall
      // back to the legacy writer otherwise so the
      // `dxfBridge.dynamic.constraints.test.ts` round-trip tests (which
      // exercise `mode: "increment"` + explicit min/max) keep passing.
      case "constraint-horizontal": {
        const obj = constraintValueSetIsBptSafe(p.distanceValueSet)
          ? writeHorizontalConstraintParameterBpt(p, ctx)
          : tryWriteTier2Parameter(p, ctx)!;
        paramHandleById.set(p.id, obj.handle);
        out.push(obj);
        break;
      }
      case "constraint-vertical": {
        const obj = constraintValueSetIsBptSafe(p.distanceValueSet)
          ? writeVerticalConstraintParameterBpt(p, ctx)
          : tryWriteTier2Parameter(p, ctx)!;
        paramHandleById.set(p.id, obj.handle);
        out.push(obj);
        break;
      }
      case "constraint-angular": {
        const obj = constraintValueSetIsBptSafe(p.angleValueSet)
          ? writeAngularConstraintParameterBpt(p, ctx)
          : tryWriteTier2Parameter(p, ctx)!;
        paramHandleById.set(p.id, obj.handle);
        out.push(obj);
        break;
      }
      case "constraint-radial": {
        // Tier 2 has no legacy radial writer (radial was introduced in
        // DYN-4 Phase 2b), so we always route to the BPT writer — the
        // older tier-3 `ACDBBLOCKCONSTRAINEDRADIAL` writer remains
        // reachable via `tryWriteTier3Parameter` for fixtures that
        // explicitly expect that class name, but for parameters authored
        // via the constraint schema we emit the AutoCAD 2018+ shape.
        const obj = writeRadialConstraintParameterBpt(p, ctx);
        paramHandleById.set(p.id, obj.handle);
        out.push(obj);
        break;
      }
      default: {
        // Try Tier 2 first, then Tier 3.
        const tier2Obj = tryWriteTier2Parameter(p, ctx);
        if (tier2Obj) {
          paramHandleById.set(p.id, tier2Obj.handle);
          out.push(tier2Obj);
          break;
        }
        const tier3Obj = tryWriteTier3Parameter(p, ctx);
        if (tier3Obj) {
          paramHandleById.set(p.id, tier3Obj.handle);
          out.push(tier3Obj);
          break;
        }
        if (typeof console !== "undefined") {
          console.warn(
            `[dxfBridge.dynamic] parameter kind unhandled: ${(p as DynamicParameterKindShape).kind}`,
          );
        }
        break;
      }
    }
  }

  for (const a of meta.actions) {
    const paramHandle = paramHandleById.get(a.parameterId);
    if (!paramHandle) {
      // Orphan action — log warning and skip. The writer does not
      // silently drop the action; the caller can detect the missing
      // emission by comparing counts. Static blocks with no parameters
      // never reach this branch.
      if (typeof console !== "undefined") {
        console.warn(
          `[dxfBridge.dynamic] action ${a.id} references unknown parameter ${a.parameterId}`,
        );
      }
      continue;
    }
    switch (a.kind) {
      case "move":
        out.push(writeMoveAction(a, paramHandle, ctx));
        break;
      case "stretch":
        out.push(writeStretchAction(a, paramHandle, ctx));
        break;
      case "flip":
        out.push(writeFlipAction(a, paramHandle, ctx));
        break;
      default: {
        const tier2Obj = tryWriteTier2Action(a, paramHandle, ctx);
        if (tier2Obj) {
          out.push(tier2Obj);
          break;
        }
        const tier3Obj = tryWriteTier3Action(a, paramHandle, ctx);
        if (tier3Obj) {
          out.push(tier3Obj);
          break;
        }
        if (typeof console !== "undefined") {
          console.warn(
            `[dxfBridge.dynamic] action kind unhandled: ${(a as DynamicActionKindShape).kind}`,
          );
        }
        break;
      }
    }
  }

  // Emit Tier 3 geometric constraints (ACDBBLOCK2DGEOMETRICCONSTRAINT).
  for (const c of meta.geometricConstraints ?? []) {
    out.push(writeGeometricConstraint(c, ctx));
  }

  // DYN-4 Phase 4b Fix 7 — emit typed grip OBJECTS records. Each grip
  // re-emits its preserved rawGroups verbatim when available so AutoCAD
  // vendor-specific codes (91/92/93/280/1071/98/99) survive round-trip
  // without requiring a full evaluator model to reconstruct. Synthetic
  // grips (constructed programmatically) fall back to the canonical
  // minimal envelope inside `writeGrip`.
  // Phase 5.1.d — route grips through the grip aspect. Aspect.write
  // delegates to writeGrip (verbatim).
  for (const grip of meta.grips ?? []) {
    const gripAspect = DYNAMIC_ASPECT_LEGACY
      ? undefined
      : findAspectById("grip");
    out.push(
      gripAspect !== undefined
        ? (gripAspect.write(grip, undefined) as DxfDynamicBlockObject)
        : writeGrip(grip),
    );
  }

  // Re-emit unsupported (future-tier) objects preserved in futureExtensions.
  const preserved =
    (meta.futureExtensions.rawObjects as DxfDynamicBlockObject[] | undefined) ??
    [];
  for (const raw of preserved) out.push(raw);

  return out;
}

// Typed shapes used for the exhaustive-switch fallbacks above. We can't
// use `never` here because the discriminated unions include Tier 2
// variants that hit the default branch; but we still want a nominal
// typed accessor for the log line.
type DynamicParameterKindShape = { kind: string };
type DynamicActionKindShape = { kind: string };

/**
 * BPT-shape-safe value-set predicate — see the constraint dispatch
 * comment in `emitDynamicBlockObjects` for motivation. Returns `true`
 * when the value set can round-trip through the BPT parser without
 * hitting the 141=default / 141=min collision.
 *
 * Three cases qualify:
 *   1. No value set object present (treat as mode: "none").
 *   2. `mode === "none"` — the parser guard `mode !== "none"` prevents
 *      min/max from being read from the value-set block.
 *   3. `min === undefined` — even with a non-none mode, if no min is
 *      authored the writer emits no 141-at-valueset line, so the first
 *      (and only) 141 in the record is the authored default.
 *
 * Parameters with `mode !== "none"` AND an authored numeric `min` fall
 * through to the legacy writer for round-trip exactness.
 */
function constraintValueSetIsBptSafe(
  vs: { mode: string; min?: number | undefined } | undefined,
): boolean {
  if (!vs) return true;
  if (vs.mode === "none") return true;
  if (vs.min === undefined) return true;
  return false;
}
